import { Breakpoints, getCookie } from '@pb/utils'
import { curry, filter, propEq } from 'ramda'
import { createSelector } from 'reselect'

import { ADDRESS_FORMAT_2_COUNTRIES, CreditCardType } from 'features/checkout/constants'
import { RootState } from 'redux/types'

import {
  CUSTOMER_TYPES,
  ACCOUNT_HOLDER_STATUSES,
  USER_CLUB_JOIN_STATUS,
  CHECKOUT_ROUTES,
} from '../constants'
import {
  AlternativeId,
  AppState,
  ClubSetup,
  CurrentCurrency,
  FieldConfig,
  FieldsOrdering,
  PayLaterConfiguration,
  Titles,
} from './types'
import { Country } from './types/country'
import { User } from './types/user'
import { getReactLocale } from 'helpers/getReactLocale'

const getAppState = (state): AppState => state.app

const getScreenSizeState = (state): Breakpoints => getAppState(state).screenSize

const isSettingsEnabled = process.env.IS_SETTINGS_API_ENABLED === 'enabled'
const isUsersEnabled = process.env.IS_USER_API_ENABLED === 'enabled'

const getShowLoaders = (state): boolean => getAppState(state).showLoaders

const getIsSettingsLoading = (state): any =>
  getShowLoaders(state) || getAppState(state).isSettingsLoading
const getSettings = (state): any => getAppState(state).settings
const getConfig = (state): any => getAppState(state).config
const getHasConfigLoaded = (state): boolean => Object.keys(getConfig(state)).length > 0
const getIsTargetApp = (state): boolean => !!getSettings(state)?.targetApp
const getKlarnaMinAge = (state): number => getSettings(state).omniCreditMinAge
const getIsGiftCardEnabled = (state): boolean => !!getSettings(state)?.giftCardEnabled
const getIsFashionNewsEnabled = (state): boolean => !!getSettings(state)?.fashionNewsEnabled
const getPrivacyPolicyURL = (state): string => getSettings(state).privacyPolicyURL
const getPrivacyNoticeHmClubLink = (state): string => getSettings(state).privacyNoticeHmClubLink
const getPrivacyNoticeAccountLink = (state): string => getSettings(state).privacyNoticeAccountLink
const getPrivacyNoticeDirectMarketingLink = (state): string =>
  getSettings(state).privacyNoticeDirectMarketingLink
const getDataProcessingConsentLink = (state): string => getSettings(state).dataProcessingConsentLink
const getOverseasTransferConsentLink = (state): string =>
  getSettings(state).overseasTransferConsentLink
const getThirdPartyConsentLink = (state): string => getSettings(state).thirdPartyConsentLink
const getPersonalInfoConsentLink = (state): string => getSettings(state).personalInfoConsentLink
const getClubTermsAndConditionURL = (state): string => getSettings(state).clubTermsAndConditionURL
const getTermsAndConditionsLink = (state): string => getSettings(state).termsAndConditionsLink
const getCustomerServiceAEMLink = (state): string => getSettings(state).customerServiceLink
const getMarketingInformationConsentLink = (state): string =>
  getSettings(state).marketingInformationConsentLink

const getHasSettingsLoaded = (state): boolean => Object.keys(getSettings(state)).length > 0
const getClubSetup = (state): ClubSetup | undefined => getSettings(state).clubSetup || undefined
const getBonusVoucherPointsInterval = (state): number | undefined =>
  getClubSetup(state)?.cashBackPoints
const getMaxNumberOfBonusVouchersPerYear = (state): number | undefined =>
  getClubSetup(state)?.cashBackCapYear
const getCurrentCurrency = (state): CurrentCurrency => getSettings(state).currentCurrency
const getIsRateAndReviewEnabled = (state): boolean => !!getSettings(state)?.rateAndReviewEnabled
const getShouldFetchSettings = (state): boolean => !getHasSettingsLoaded(state)
const getIsDigitalReceiptEnabled = (state): boolean => !!getSettings(state)?.digitalReceiptEnabled
const getIsDigitalReceiptOptInEnabled = (state): boolean => !!getSettings(state)?.togglePrintReceipt
const getIsOnlineReceiptEnabled = (state): boolean => !!getSettings(state)?.onlineReceiptEnabled
const getIsClubMarket = (state): boolean => !!getSettings(state)?.loyaltyEnabled
const getIsCiamEnabled = (state): boolean => !!getSettings(state)?.ciamEnabled
const getIsGuestCheckoutEnabled = (state): boolean => !!getSettings(state)?.guestCheckoutEnabled
const getIsOneClickCheckoutEnabled = (state): boolean => !!getSettings(state)?.occEnabled
const getPayPalEnabled = (state): boolean => !!getSettings(state)?.payPalEnabled
const getPayPalExpressEnabledMap = (state): { cart: boolean; loginCheckout: boolean } =>
  getSettings(state).payPalExpressEnabledMap
const getIsKakaoEnabled = (state): boolean => !!getSettings(state)?.kakaoEnabled
const getIsLoginCheckoutDecouplingEnabled = (state): boolean =>
  !!getSettings(state)?.loginCheckoutDecouplingEnabled
const getIsPcmiEnabled = (state): boolean => !!getSettings(state)?.pcmiEnabled
const getIsSavePaymentCardAccountEnabled = (state): boolean =>
  !!getSettings(state)?.savePaymentCardAccountEnabled
const getShouldShowBankAccountInfo = (state): boolean =>
  !!getSettings(state)?.showBankAccountInformation
const getReturnAndRefundUrl = (state): string => getSettings(state).returnAndRefundUrl
const getAlternativeId = (state): AlternativeId | undefined =>
  getSettings(state).alternativeId || undefined
const getMinAge = (state): number => getSettings(state).minAge
const getSsiContextPath = (state): string => getSettings(state).ssiContextPath
const getFieldsConfig = (state): FieldConfig[] => getSettings(state).fieldsConfiguration
const getFieldsOrdering = (state): FieldsOrdering => getSettings(state).fieldsOrdering
const getBillingCountries = (state): Country[] => getSettings(state).billingCountries
const getIsStyleboardEnabled = (state): boolean => !!getSettings(state)?.styleboardsEnabled
const getIsPssEnabledAtStoreLevel = (state): boolean => !!getSettings(state)?.pssEnabledAtStoreLevel

const getIsDesktop = (state): boolean =>
  getScreenSizeState(state) === Breakpoints.XLARGE || getScreenSizeState(state) === Breakpoints.XXL
const getIsTablet = (state): boolean => getScreenSizeState(state) === Breakpoints.LARGE
const getIsMobile = (state): boolean => getScreenSizeState(state) === Breakpoints.MEDIUM
const getIsXSmall = (state): boolean =>
  getScreenSizeState(state) === (Breakpoints.SMALL || Breakpoints.XSMALL)
const getIsSmallScreen = (state): boolean => getIsXSmall(state) || getIsMobile(state)
const getIsLargeScreen = (state): boolean => getIsTablet(state) || getIsDesktop(state)

const getXHRErrors = (state): any => getAppState(state).xhrErrorCodes
const getXHRErrorByStatusCode = curry((statusCode, state) => getXHRErrors(state)[statusCode])
const getIsUnauthorized = (state): boolean => !!getXHRErrors(state)[401]

const getUser = (state): any => getAppState(state).user || {}
const getUserId = (state): any => getUser(state).customerId
const getIsSignedIn = (state): boolean =>
  !!getUserId(state) && getUser(state).customerType === CUSTOMER_TYPES.REGISTERED

const getIsCleansingRequested = (state): boolean => getUser(state).cleansingRequested || false
const getUserFashionNewsSubscriptionStatus = (state): any => getUser(state).customerFashionNews
const getIsUserSubscribedToFashionNews = (state): boolean =>
  !!getUser(state).hmFashionNewsSubscription
const getIsUserSubscribedToClubNews = (state): boolean => !!getUser(state).hmCatalogueSubscription
const getUserFrequencyFashionNews = (state): 'REG' | 'NON' =>
  getIsUserSubscribedToFashionNews(state) ? 'REG' : 'NON'
const getUserFrequencyClubNews = (state): 'REG' | 'NON' =>
  getIsUserSubscribedToClubNews(state) ? 'REG' : 'NON'

const getCustomerLoyaltyId = (state): any => getUser(state).customerLoyaltyId
const getCustomerLoyaltyLevel = (state): any => getUser(state).customerLoyaltyLevel
const getCustomerType = (state): any => getUser(state).customerType
const getUserCategoryId = (state): any => getUser(state).categoryId
const getIsUserLoading = (state): boolean =>
  getShowLoaders(state) || getAppState(state).isUserLoading
const getHasUserLoaded = (state): boolean => !!getUserId(state)
const getIsUserRequested = (state): boolean => getAppState(state).isUserRequested
const getIsUserLoadingFailed = (state): boolean => getAppState(state).isUserLoadingFailed
const getShouldFetchUser = (state): boolean =>
  !getHasUserLoaded(state) || getIsUserLoadingFailed(state)
const getUserCBPMember = (state): any => getUser(state).cbpcustomer
const getUserBpid = (state): any => getUser(state).customerBpid
const getUserCountry = (state): string => getSettings(state).countryName
const getUserCountryCode = (state): any => getUser(state).countryCode
const getUserFullCountryInformation = (state): Country =>
  filter(propEq('isocode', getUserCountryCode(state)))(getBillingCountries(state))[0]
const getUserCity = (state): any => getUser(state).customerCity
const getUserState = (state): any => getUser(state).customerState
const getUserPostalCode = (state): string => getUser(state).postalCode || ''
const getUserStatus = (state): any => getUser(state).status
const getValidatedUser = (state): boolean => getUser(state).validatedUser

const shouldRenderConfig = process.env.IS_CONFIG_API_ENABLED === 'enabled'

const isSettingsApiFallbackEnabled = process.env.IS_SETTINGS_API_FALLBACK_ENABLED === 'enabled'
const getIsInitialLoadCompleted = (state): boolean => {
  if (isSettingsEnabled && isSettingsApiFallbackEnabled) {
    return (
      ((isUsersEnabled ? getHasUserLoaded(state) : true) || getIsUserLoadingFailed(state)) &&
      (shouldRenderConfig ? getHasConfigLoaded(state) : true)
    )
  }

  return (
    // Need to have fallback as true when IS_SETTINGS_API_ENABLED and IS_USER_API_ENABLED is disabled because we still want to render the page.
    (isSettingsEnabled ? getHasSettingsLoaded(state) : true) &&
    ((isUsersEnabled ? getHasUserLoaded(state) : true) || getIsUserLoadingFailed(state)) &&
    (shouldRenderConfig ? getHasConfigLoaded(state) : true)
  )
}

const getIsUserClubMember = (state): boolean =>
  getIsUserRequested(state) && ACCOUNT_HOLDER_STATUSES.includes(getUser(state).status)

const getIsUserOldAccountHolder = (state): boolean => !getIsUserClubMember(state)
const getIsUserOldAndRegisteredAccountHolder = createSelector(
  getIsUserOldAccountHolder,
  getCustomerType,
  (isUserOldAccountHolder, customerType) => {
    return isUserOldAccountHolder && customerType === CUSTOMER_TYPES.REGISTERED
  },
)

const getUserIsFullMember = (state): boolean =>
  getUser(state).status === USER_CLUB_JOIN_STATUS.FULL_MEMBER
const getUserIsPendingMember = (state): boolean =>
  getUser(state).status === USER_CLUB_JOIN_STATUS.PENDING ||
  getUser(state).status === USER_CLUB_JOIN_STATUS.INITIAL // We handle "INITIAL" members the same way as "PENDING"
const getIsStaffMember = (state): boolean => !!getUser(state).staffCardNumber
const getUserFirstName = (state): string => getUser(state).firstName || ''
const getUserEmail = (state): string => getUser(state).email || ''
const getUserDisplayName = (state): string => getUser(state).displayName || ''
const getUserBirthDate = (state): any => getUser(state).birthDate || {}
const getUserBirthDateISO = (state): string => {
  const { day, month, year } = getUserBirthDate(state)
  if (day && month && year) {
    return `${year}-${month}-${day}`
  }

  return ''
}
const getUserCommunityNickname = (state): string => getUser(state).communityNickname || ''

const getIsUserRegisteredGuest = (state): boolean =>
  getCustomerType(state) === CUSTOMER_TYPES.GUEST && getUserDisplayName(state).length > 0
const getUserHasChildren = (state): any => getUser(state).customerHasChildren || false
const getEmailConfirmationState = (state): any => getUser(state).emailConfirmationState
const getIsEmailConfirmationStateUnconfirmed = (state): boolean =>
  getEmailConfirmationState(state) === 'UNCONFIRMED'
const getLinkedSMProfileForUser = (state): any => getUser(state).linkedSMProfile

const getIsPayLaterWhitelistedUser = (state): boolean => !!getUser(state)?.payLaterWhitelisted
const getPayLaterConfiguration = (state): PayLaterConfiguration | undefined =>
  getSettings(state).payLaterConfiguration || undefined
const getIsPayLaterEnabled = (state): boolean =>
  !!getPayLaterConfiguration(state)?.payLaterMarket || getIsPayLaterWhitelistedUser(state)
const getIsPayLaterActivationEnabled = (state): boolean =>
  getIsPayLaterEnabled(state) && !!getPayLaterConfiguration(state)?.activationEnabled
const getIsPayLaterActivatedForUser = (state): boolean =>
  getUser(state).omnicreditStatus === 'AUTHENTICATED'
const getIsPayLaterNotActivatedForUser = (state): boolean =>
  getUser(state).omnicreditStatus === 'NOT_AUTHENTICATED'
const getIsPayLaterNotAllowedForUser = (state): boolean =>
  getUser(state).omnicreditStatus === 'NOT_ALLOWED' || !getUser(state).omnicreditStatus
const getIsPayLaterDeactivatedForUser = (state): boolean =>
  getUser(state).omnicreditStatus === 'DEACTIVATED'
const getIsPayLaterActivationAllowed = (state): boolean =>
  getIsPayLaterActivationEnabled(state) && !getIsPayLaterNotAllowedForUser(state)
const getIsPayLaterPostPurchaseEnabled = (state): boolean =>
  getIsPayLaterEnabled(state) && !!getPayLaterConfiguration(state)?.postPurchaseEnabled
const getPayLaterCartUpstreamMarketingEnabled = (state): boolean =>
  !!getPayLaterConfiguration(state)?.cartUpstreamMarketingEnabled
const getPayLaterManageCardEnabled = (state): boolean =>
  !!getPayLaterConfiguration(state)?.manageCardEnabled
const getKlarnaClientId = (state): string | undefined =>
  getPayLaterConfiguration(state)?.klarnaClientId
const getKlarnaPostPurchaseScriptUrl = (state): string | undefined =>
  getPayLaterConfiguration(state)?.klarnaPostPurchaseScriptUrl
const getIsPostPurchaseAuthenticated = (state): boolean => getUser(state).postPurchaseAuthenticated
const getIsUserOmnicreditCustomer = (state): boolean => getUser(state).omniCreditCustomer
export const getShouldShowPayLaterPostPurchaseMenuItem = (state): boolean =>
  getIsPayLaterPostPurchaseEnabled(state) &&
  (getIsUserOmnicreditCustomer(state) || getIsPayLaterActivatedForUser(state))
const getShowAlerts = (state): boolean => !!getSettings(state)?.showAlerts

const getShouldShowExtraConsents = (state): boolean => !!getSettings(state)?.showExtraConsens

const getTitles = (state): Titles | undefined => getSettings(state).titleList || undefined

const getCardholderCountries = (state): Country[] => getSettings(state).billingCountries || []

const getCardholderCountryByIsoCode = (isoCode: string): ((state: RootState) => Country | {}) =>
  createSelector(
    getCardholderCountries,
    (countries) => countries.find((country) => country.isocode === isoCode) || {},
  )

const getProvinces = (state): Record<string, string> => getSettings(state).provinceList || {}
const getGeoBlockingCountries = (state): Country[] => getSettings(state).geoBlockingCountries || []

const getGeoBlockingCountryByIsoCode = (isoCode): ((state: RootState) => Country | {}) =>
  createSelector(
    getGeoBlockingCountries,
    (countries) => countries.find((country) => country.isocode === isoCode) || {},
  )
const getIsGeoBlockingDisabled = (state): boolean => !!getSettings(state)?.geoBlockingDisabled

// Primary market source is BE user API, if that is missing - look at URL.
export const getMarketCode = createSelector(getUser, (user: User): string => {
  const locale = getReactLocale()?.split('_')?.[1]
  return user.selectedMarket || locale || ''
})

const getIsPointsHistoryEnabled = (state): boolean => !!getSettings(state)?.pointsHistory
const getPointsExpirationDate = (state): any => getUser(state).pointsExpirationDate
const getShouldShowClubTermsAndConditionModal = (state): boolean =>
  !!getUser(state)?.showClub2TermsAndConditionModal
const getShouldDisplayDoubleOptIn = (state): boolean =>
  getUser(state).doiMarket && getIsEmailConfirmationStateUnconfirmed(state)

const getFeatureToggleFlags = (state): any => getAppState(state).featureToggleFlags
const getShouldFetchFeatureToggleFlags = (state): any =>
  getFeatureToggleFlags(state).shouldFetchFeatureToggleFlags
const getIsDigitalStylistEnabled = (state): boolean =>
  !!getSettings(state).digitalStyleEnabled?.digitalStylistEnabledForWEB

const getIsInviteFriendEnabled = (state): any =>
  getClubSetup(state) && !!getClubSetup(state)?.inviteAFriendEnabled
const getIsLegalReceiptEnabled = (state): boolean => !!getSettings(state)?.legalReceiptEnabled

const getIsDecoupledCheckoutEnabled = (state): boolean => {
  const decoupledCheckoutStatus = String(getSettings(state).checkoutDecouplingEnabled)
  const isCompletelyEnabledInMarket = decoupledCheckoutStatus === 'true'
  const isPartlyEnabledInMarket = decoupledCheckoutStatus === 'AB'
  const isEnabledForCustomer = isPartlyEnabledInMarket && getCookie('ab_idce') === 'TRUE'

  return isCompletelyEnabledInMarket || isEnabledForCustomer
}

const getPreviousFNSubscribeState = (state): boolean => getAppState(state).previousFNSubscribeState

const getIsSendAgainClickedForCurrentSession = (state): boolean =>
  getAppState(state).isSendAgainClicked

const getIsSendAgainAlreadyClickedForCurrentSession = (state): boolean =>
  getAppState(state).isSendAgainAlreadyClicked

const getIsChallengesBannerTrackingSent = (state): boolean =>
  getAppState(state).isChallengesBannerTrackingSent

const getIsAbTestVoucherModalEnabled = (): any => getCookie('ab_test_voucher_modal') === 'true'

const getPasswordFieldMinLength = createSelector(getFieldsConfig, (fieldsConfig) => {
  const passwordConfig = fieldsConfig?.find((config) => config.attribute === 'password')
  return passwordConfig?.validation.minLength
})
const getPasswordFieldMaxLength = createSelector(getFieldsConfig, (fieldsConfig) => {
  const passwordConfig = fieldsConfig?.find((config) => config.attribute === 'password')
  return passwordConfig?.validation.maxLength
})
const getPhoneNumberFormat = createSelector(getFieldsConfig, (fieldsConfig) => {
  const phoneFieldConfig = fieldsConfig?.find((field) => field.attribute === 'cellPhone')

  return phoneFieldConfig?.validation.format
})

// Used on Serbia when PostalCode is fetched through an API
export const getIsPostalCodeAddressFieldDisabled = createSelector(
  getFieldsConfig,
  (fieldsConfig) => {
    const postalCodeFieldConfig = fieldsConfig?.find(
      (field) => field?.type?.name === 'CHECKOUTADDRESS' && field.attribute === 'postalCode',
    )

    return !!(postalCodeFieldConfig?.disabled && postalCodeFieldConfig?.visible)
  },
)

export const hasProvinceDropdownFieldInCO = createSelector(getFieldsConfig, (fieldsConfig) => {
  const districtFieldConfig = fieldsConfig?.find(
    (field) => field?.type?.name === 'CHECKOUTADDRESS' && field.attribute === 'province',
  )
  return (districtFieldConfig?.fieldType?.name as string) === 'COMBOBOX'
})

export const hasTownDropdownFieldInCO = createSelector(getFieldsConfig, (fieldsConfig) => {
  const districtFieldConfig = fieldsConfig?.find(
    (field) => field?.type?.name === 'CHECKOUTADDRESS' && field.attribute === 'town',
  )
  return (districtFieldConfig?.fieldType?.name as string) === 'COMBOBOX'
})

export const hasDistrictDropdownFieldInCO = createSelector(getFieldsConfig, (fieldsConfig) => {
  const districtFieldConfig = fieldsConfig?.find(
    (field) => field?.type?.name === 'CHECKOUTADDRESS' && field.attribute === 'district',
  )
  return (districtFieldConfig?.fieldType?.name as string) === 'COMBOBOX'
})

const getGiftCardNumberLength = (state): number => getSettings(state).giftCardLength
const getGiftCardPinLength = (state): number => getSettings(state).giftCardPinLength
const getMaxNumberOfGiftCards = (state): number => getSettings(state).maxGiftCard
const getMaxNumberOfCreditCards = (state): number => getSettings(state).maxCreditCard
const getSupportedCreditCards = (state): CreditCardType[] => getSettings(state).supportedCreditCards

const getMinAgeNewsletterSubscription = (state): number => getSettings(state).minAgeNewsletterSubsc

const getModalError = (state): any => getAppState(state)?.errorModal?.error

const getAppPageViewTrackingData = (state): any => {
  /* eslint-disable @typescript-eslint/naming-convention */
  const category_path = getSsiContextPath(state)
  const selected_market = getMarketCode(state)
  const customer_bpid = getUserBpid(state)
  const customer_id = getUserId(state)
  const customer_city = getUserCity(state)
  const customer_country = getUserCountryCode(state)
  const customer_email = getUserEmail(state)
  const customer_state = getUserState(state)
  const customer_zip = getUserPostalCode(state)
  const customer_fashion_news = getIsUserSubscribedToFashionNews(state)
  const customer_loyalty_level = getCustomerLoyaltyLevel(state)
  const customer_has_children = getUserHasChildren(state)
  const customer_status = getUserStatus(state)
  const customer_club_member = getUserIsFullMember(state)

  const customer_fashion_news_optin = getEmailConfirmationState(state)
  /* eslint-enable @typescript-eslint/naming-convention */

  return {
    category_path,
    selected_market,
    customer_bpid,
    customer_id,
    customer_city,
    customer_country,
    customer_email,
    customer_state,
    customer_zip,
    customer_fashion_news,
    customer_loyalty_level,
    customer_has_children,
    customer_status,
    customer_club_member,
    customer_fashion_news_optin,
  }
}

const getCheckoutPath = createSelector(
  getIsDecoupledCheckoutEnabled,
  (isDecoupledCheckoutEnabled) =>
    isDecoupledCheckoutEnabled
      ? CHECKOUT_ROUTES.CheckoutOverview.reroutePath
      : CHECKOUT_ROUTES.CheckoutOverview.path,
)

const getIsExternalTaxApplied = createSelector(
  getSettings,
  (settings) => !!settings.externalTaxApplied,
)
const getIsVatNumberEnabled = createSelector(getSettings, (settings) => !!settings.vatNumberEnabled)

const getVatInvoiceTypeMap = createSelector(getSettings, (settings) => {
  if (settings.invoiceTypeMap) {
    const invoiceTypeKeys = Object.keys(settings.invoiceTypeMap)
    return invoiceTypeKeys.map((key) => ({ label: settings.invoiceTypeMap[key], value: key }))
  }
  return settings.invoiceTypeMap
})

export const getIsDefaultAddressFormat = createSelector(getMarketCode, (code) => {
  return !ADDRESS_FORMAT_2_COUNTRIES.includes(code as string)
})

const getUserCustomerCorePortalData = (state): string => getUser(state).customerCorePortalData || ''

const getDirectMailMarketingEnabled = (state): boolean =>
  getSettings(state).directMailMarketingEnabled

const getShouldPassCityWithPSS = (state?: RootState): boolean =>
  !!getSettings(state)?.pssAddCityforPickupPlaces

const getShouldPassProvinceWithPSS = (state?: RootState): boolean =>
  !!getSettings(state)?.pssAddProvinceforPickupPlaces

export const getIsGoogleAutoCompleteEnabled = createSelector(
  getSettings,
  (settings): boolean => settings.googleAutoCompleteEnabled,
)

export const getIsSplitDeliveryEnabled = createSelector(
  getSettings,
  (settings): boolean => settings?.splitDeliveryEnabled,
)

export const getIsSplitDeliveryEnabledMap = createSelector(
  getSettings,
  (settings): boolean => settings?.splitDeliveryEnabledMap,
)

export const getMiddlePageEmail = createSelector(
  getAppState,
  (appState) => appState?.middlePageEmail,
)

export default {
  getShouldDisplayDoubleOptIn,
  getScreenSizeState,
  getIsDesktop,
  getIsTablet,
  getIsMobile,
  getIsXSmall,
  getIsInitialLoadCompleted,
  getIsSettingsLoading,
  getSettings,
  getConfig,
  getIsTargetApp,
  getFieldsConfig,
  getFieldsOrdering,
  getHasSettingsLoaded,
  getClubSetup,
  getBonusVoucherPointsInterval,
  getMaxNumberOfBonusVouchersPerYear,
  getCurrentCurrency,
  getIsRateAndReviewEnabled,
  getShouldFetchSettings,
  getIsDigitalReceiptEnabled,
  getIsDigitalReceiptOptInEnabled,
  getIsOnlineReceiptEnabled,
  getIsClubMarket,
  getIsGuestCheckoutEnabled,
  getShowAlerts,
  getIsOneClickCheckoutEnabled,
  getPayPalEnabled,
  getPayPalExpressEnabledMap,
  getAlternativeId,
  getIsKakaoEnabled,
  getIsLoginCheckoutDecouplingEnabled,
  getIsPcmiEnabled,
  getIsSavePaymentCardAccountEnabled,
  getShouldShowBankAccountInfo,
  getReturnAndRefundUrl,
  getMinAge,
  getSsiContextPath,
  getIsSmallScreen,
  getIsLargeScreen,
  getShowLoaders,
  getXHRErrors,
  getXHRErrorByStatusCode,
  getIsUnauthorized,
  getIsSignedIn,
  getIsUserRegisteredGuest,
  getUser,
  getUserId,
  getCustomerLoyaltyId,
  getCustomerType,
  getUserCategoryId,
  getIsUserLoading,
  getHasUserLoaded,
  getShouldFetchUser,
  getUserCBPMember,
  getUserBpid,
  getUserCountry,
  getUserCountryCode,
  getUserCity,
  getUserState,
  getUserPostalCode,
  getIsUserOldAccountHolder,
  getIsUserOldAndRegisteredAccountHolder,
  getUserIsFullMember,
  getUserIsPendingMember,
  getUserFashionNewsSubscriptionStatus,
  getIsUserSubscribedToFashionNews,
  getIsUserSubscribedToClubNews,
  getUserFrequencyFashionNews,
  getUserFrequencyClubNews,
  getUserEmail,
  getUserBirthDate,
  getUserBirthDateISO,
  getIsStaffMember,
  getUserFirstName,
  getUserDisplayName,
  getIsUserLoadingFailed,
  getEmailConfirmationState,
  getIsEmailConfirmationStateUnconfirmed,
  getLinkedSMProfileForUser,
  getIsPostPurchaseAuthenticated,
  getShouldShowExtraConsents,
  getIsPayLaterEnabled,
  getIsPayLaterActivationEnabled,
  getIsPayLaterActivatedForUser,
  getIsPayLaterNotActivatedForUser,
  getIsPayLaterNotAllowedForUser,
  getIsPayLaterDeactivatedForUser,
  getIsPayLaterActivationAllowed,
  getIsPayLaterPostPurchaseEnabled,
  getPayLaterCartUpstreamMarketingEnabled,
  getPayLaterManageCardEnabled,
  getKlarnaClientId,
  getKlarnaPostPurchaseScriptUrl,
  getIsPointsHistoryEnabled,
  getPointsExpirationDate,
  getShouldFetchFeatureToggleFlags,
  getShouldShowClubTermsAndConditionModal,
  getIsDigitalStylistEnabled,
  getIsInviteFriendEnabled,
  getTitles,
  getPrivacyPolicyURL,
  getTermsAndConditionsLink,
  getCustomerServiceAEMLink,
  getPrivacyNoticeHmClubLink,
  getPrivacyNoticeAccountLink,
  getPrivacyNoticeDirectMarketingLink,
  getDataProcessingConsentLink,
  getOverseasTransferConsentLink,
  getThirdPartyConsentLink,
  getMarketingInformationConsentLink,
  getPersonalInfoConsentLink,
  getClubTermsAndConditionURL,
  getIsFashionNewsEnabled,
  getIsLegalReceiptEnabled,
  getIsCleansingRequested,
  getUserHasChildren,
  getUserStatus,
  getCustomerLoyaltyLevel,
  getIsUserClubMember,
  getIsDecoupledCheckoutEnabled,
  getPasswordFieldMinLength,
  getPasswordFieldMaxLength,
  getPhoneNumberFormat,
  getIsAbTestVoucherModalEnabled,
  getIsGiftCardEnabled,
  getBillingCountries,
  getIsStyleboardEnabled,
  getUserFullCountryInformation,
  getGiftCardNumberLength,
  getGiftCardPinLength,
  getMaxNumberOfGiftCards,
  getGeoBlockingCountries,
  getIsGeoBlockingDisabled,
  getGeoBlockingCountryByIsoCode,
  getProvinces,
  getCardholderCountryByIsoCode,
  getKlarnaMinAge,
  getMinAgeNewsletterSubscription,
  getMaxNumberOfCreditCards,
  getSupportedCreditCards,
  getModalError,
  getIsCiamEnabled,
  getAppPageViewTrackingData,
  getCheckoutPath,
  getIsExternalTaxApplied,
  getIsVatNumberEnabled,
  getVatInvoiceTypeMap,
  getPreviousFNSubscribeState,
  getIsSendAgainClickedForCurrentSession,
  getIsSendAgainAlreadyClickedForCurrentSession,
  getValidatedUser,
  getUserCustomerCorePortalData,
  getUserCommunityNickname,
  getIsPssEnabledAtStoreLevel,
  getDirectMailMarketingEnabled,
  getIsUserOmnicreditCustomer,
  getIsChallengesBannerTrackingSent,
  getShouldPassCityWithPSS,
  getShouldPassProvinceWithPSS,
}
